import { Seo } from '../components/Seo';
import { Section } from '../components/Section';
import { Counter, DrawOnView, Reveal, TiltCard } from '../components/motion';
import { Accordion } from '../components/Accordion';
import { OutcomeBand } from '../components/charts';
import { STAGES } from '../data/site';
import {
  ArrowLink,
  ButtonLink,
  Card,
  Display,
  Eyebrow,
  HeroTitle,
  InkPanel,
  Lede,
  RungBadge,
  ScrollableTable,
} from '../components/ui';

const RUN_SPEC = [
  { label: 'Wall time', value: '5–20 min' },
  { label: 'Stages', value: '9' },
  { label: 'Assumptions surfaced', value: '5–8' },
  { label: 'Experiments returned', value: '3' },
  { label: 'Score returned', value: 'none' },
];

const OUTPUTS = [
  {
    n: 'i',
    title: 'The distribution of outcomes',
    body: ', with the probability of clearing a viability threshold the founder stated.',
  },
  {
    n: 'ii',
    title: 'The five to eight assumptions',
    body: ' the outcome actually depends on, each showing where its number came from.',
  },
  {
    n: 'iii',
    title: 'Kill-lines.',
    body: ' For each assumption, the numeric threshold at which the venture stops working, and the probability the truth sits on the wrong side of it.',
  },
  {
    n: 'iv',
    title: 'The next three experiments',
    body: ', with cost, duration, and the result that would end the idea.',
  },
  {
    n: 'v',
    title: 'A structural verdict',
    body: ' that is allowed to disagree with the numbers, and says so when it does.',
  },
  {
    n: 'vi',
    title: 'Adjacent versions of the idea',
    body: ' that beat the original on its weakest assumptions.',
  },
  {
    n: 'vii',
    title: 'An evidence grade',
    body: ' on the whole run, reflecting the quality of its inputs.',
  },
];

const RUNGS = [
  { rung: 'P1', name: 'Measured', body: 'The figure was observed, in a source you can open.' },
  {
    rung: 'P2–P5',
    name: 'Modelled',
    body: 'Derived from sourced constants, with the derivation shown.',
  },
  {
    rung: 'P6–P7',
    name: 'Asserted',
    body: 'Stated by a person. Labelled as such everywhere it appears.',
  },
];

const KILL_LINES = [
  {
    assumption: 'Paid conversion on the acquisition channel',
    line: 'below a stated floor',
    rung: 'P3',
    evidence: 'modelled',
    side: 'high',
  },
  {
    assumption: 'Contribution margin at target volume',
    line: 'below break-even',
    rung: 'P1',
    evidence: 'measured',
    side: 'low',
  },
  {
    assumption: 'Time from signup to first value',
    line: '[FOUNDER TO FILL]',
    rung: null,
    evidence: 'unsourced — not modelled',
    side: '—',
  },
  {
    assumption: 'Retention at month twelve',
    line: 'below a stated floor',
    rung: 'P6',
    evidence: 'asserted',
    side: 'unknown',
  },
];

const USE_WHEN = [
  'You have stated a viability threshold you would actually walk away from.',
  'You are choosing between two or three versions of the same idea.',
  'You need to know which experiment to run next, not whether to feel optimistic.',
  'You are writing a memo someone else will interrogate line by line.',
];

const DO_NOT_USE = [
  'A number to put in a deck without the sentence that qualifies it.',
  'A verdict to settle an argument between founders.',
  'An idea stated too loosely to test — that returns a refusal, and no charge.',
  "Anything that needs a score out of ten. There isn't one.",
];

const ATTESTATION_STATS = [
  {
    value: 9807,
    figure: '9,807',
    label: 'contributing swarm classes',
    body: 'Real algorithm, distinct from its siblings, and demonstrably changes the output when the input changes — 47.1% of the 20,821 that are live and callable.',
  },
  {
    value: 290,
    prefix: '~',
    figure: '~290',
    label: 'agents that reach the engine',
    body: 'Of 404 registered, after removing 26 domain clones and 28 communication agents that emit no force multipliers. 311 have distinct execute bodies.',
  },
  {
    value: 9,
    figure: '9',
    label: 'cognitive layers',
    body: 'Audited 2026-05-26 against two named reports, each figure carrying the document and the quoted sentence it came from.',
  },
];

const FAQS = [
  {
    q: 'Why twelve minutes instead of twelve seconds?',
    a: (
      <p>
        Stage six runs the causal graph forward many times to produce an outcome band rather than a
        point estimate. That is the expensive stage, and it is the reason a run is measured in
        minutes. The alternative is a fast number nobody can check.
      </p>
    ),
  },
  {
    q: 'Will it tell me whether to build the thing?',
    a: (
      <p>
        No. It returns the ordered queue of things that could kill it, each with the numeric
        threshold at which it does. The verdict stays yours; the report is what you argue with.
      </p>
    ),
  },
  {
    q: 'What happens when a number does not exist?',
    a: (
      <p>
        It prints <code className="font-mono text-[0.85em]">[FOUNDER TO FILL]</code> and refuses to
        model one. That is a designed, rendered state — not an error — and it is enforced at four
        layers, including a database tripwire.
      </p>
    ),
  },
  {
    q: 'Can I share the report?',
    a: (
      <p>
        Yes. Every run has a permanent URL, and the report is built to be forwarded to someone who
        wants to argue with it. Every figure carries its rung in the page, so the argument starts at
        the source instead of at your credibility.
      </p>
    ),
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="A de-risking instrument for stated business ideas"
        description="The Startup Idea Validator spends five to twenty minutes on a business idea and returns the distribution of outcomes, the five to eight assumptions it depends on, and the numeric threshold at which each one stops working. No score."
      />

      {/* Hero */}
      <Section tone="paper" hero marquee>
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-14">
          <div>
            <Eyebrow className="text-accent-2">
              Universal framework · Assumption engineering
            </Eyebrow>
            <HeroTitle>It does not return a score. Nobody can rate an idea.</HeroTitle>
            <div className="prose-siv text-muted mt-7 flex flex-col gap-4 text-[1.0625rem] leading-[1.65] sm:text-lg">
              <p>
                A founder submits a business idea. The Startup Idea Validator spends five to twenty minutes on it and
                returns a de-risking instrument: the distribution of outcomes, the five to eight
                assumptions the outcome actually depends on, and the numeric threshold at which each
                one stops working.
              </p>
              <p>
                The verdict is never the product. The ordered queue of things that could kill it is.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
              <ButtonLink to="/contact">Submit an idea</ButtonLink>
              <ArrowLink to="/report">See a finished report</ArrowLink>
            </div>
          </div>

          <HeroDiagram />
        </div>

        <div className="border-line mt-14 max-w-2xl border-l-2 pl-5 sm:pl-6">
          <h2 className="eyebrow text-muted mb-4 font-mono">What a run costs you</h2>
          <dl>
            {RUN_SPEC.map((row) => (
              <div
                key={row.label}
                className="border-line flex items-baseline justify-between gap-6 border-b py-3"
              >
                <dt className="text-[0.975rem]">{row.label}</dt>
                <dd className="font-mono text-muted text-sm">{row.value}</dd>
              </div>
            ))}
          </dl>
          <p className="text-muted mt-4 text-sm leading-[1.65]">
            Too vague to test? You get the two sentences that would fix it, and the run is not
            charged.
          </p>
        </div>
      </Section>

      {/* What comes back */}
      <Section tone="red" id="instrument">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <Eyebrow>The instrument</Eyebrow>
            <Display>Seven things come back. None of them is a rating.</Display>
            <Lede className="!text-muted">
              Each item exists because a founder can act on it the same week. If an output could not
              change a decision, it is not in the report.
            </Lede>
          </Reveal>

          <Reveal as="ol" delay={80} className="flex flex-col">
            {OUTPUTS.map((item) => (
              <li
                key={item.n}
                className="grid grid-cols-[2.5rem_1fr] gap-3 border-t border-line py-5 last:border-b"
              >
                <span className="font-mono pt-1 text-xs text-faint">{item.n}</span>
                <p className="leading-[1.65]">
                  <strong className="font-semibold">{item.title}</strong>
                  {item.body}
                </p>
              </li>
            ))}
          </Reveal>
        </div>
      </Section>

      {/* Evidence standard */}
      <Section tone="ink">
        <Reveal>
          <Eyebrow>The evidence standard</Eyebrow>
          <Display>Every number carries a rung, and the code will not let you forget.</Display>
          <Lede>
            A validation is a set of numbers about a future nobody has observed. The only thing
            separating it from a confident fabrication is that every one of those numbers says where
            it came from and how much to trust it. So the primitive that holds a figure refuses to
            hold a value without a prior-ladder rung and a provenance block naming a resolvable
            source.
          </Lede>
        </Reveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RUNGS.map((r) => (
            <div key={r.rung} className="siv-card siv-card-hover p-6">
              <RungBadge rung={r.rung} />
              <h3 className="mt-3 text-lg font-sans font-bold tracking-normal">{r.name}</h3>
              <p className="text-muted mt-2 text-[0.95rem] leading-[1.6]">{r.body}</p>
            </div>
          ))}
          <div className="siv-card border-dashed p-6">
            <p className="font-mono text-muted border-b border-dashed border-current pb-0.5 text-[0.6875rem] tracking-[0.14em]">
              [FOUNDER TO FILL]
            </p>
            <h3 className="mt-3 text-lg font-sans font-bold tracking-normal">
              The fourth option, which is none
            </h3>
            <p className="text-muted mt-2 text-[0.95rem] leading-[1.6]">
              Where no figure could be sourced, the Startup Idea Validator prints this and will not model one. A designed,
              rendered state — not an error.
            </p>
          </div>
        </div>

        <p className="text-muted mt-8 max-w-[62ch] text-[0.95rem] leading-[1.65]">
          Enforced at four layers, including a database tripwire: adding an unsourced prior raises
          rather than persisting. <ArrowLink to="/evidence-standard">Read the standard</ArrowLink>
        </p>
      </Section>

      {/* The run */}
      <Section tone="mist">
        <Reveal>
          <Eyebrow>The run</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <Display>Nine stages. Each one consumes the last.</Display>
            <ArrowLink to="/how-it-works">Stage by stage</ArrowLink>
          </div>
        </Reveal>

        <ol className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {STAGES.map((stage) => (
            <li key={stage.id}>
              <TiltCard className="h-full">
                <Card className="h-full !p-5">
                  <p className="font-mono text-accent text-[0.6875rem] font-bold tracking-[0.16em]">
                    {stage.id}
                  </p>
                  <h3 className="mt-2.5 font-sans text-[0.975rem] font-bold tracking-normal">
                    {stage.name}
                  </h3>
                  <p className="text-muted mt-1.5 text-[0.8125rem] leading-[1.55]">{stage.blurb}</p>
                </Card>
              </TiltCard>
            </li>
          ))}
        </ol>

        <p className="text-muted mt-8 max-w-[62ch] text-[0.95rem] leading-[1.65]">
          The URL emailed at t=0 is the URL that shows the finished report at t=12min. There is no
          live-versus-report split, so reconnecting mid-run costs nothing.
        </p>
      </Section>

      {/* Sample run */}
      <Section tone="red">
        <Eyebrow>Sample run · golden fixture, not a live result</Eyebrow>
        <div className="grid gap-6 lg:grid-cols-2">
          <InkPanel>
            <ul className="mb-5 flex flex-col items-start gap-2">
              <li className="border border-line bg-surface-2 px-2.5 py-1 text-[0.8125rem] font-semibold">
                <span aria-hidden="true">◆ </span>Numeric read: does not clear
              </li>
              <li className="border border-line bg-surface-2 px-2.5 py-1 text-[0.8125rem] font-semibold">
                <span aria-hidden="true">▲ </span>Structural read: favourable, asymmetric
              </li>
            </ul>
            <h2 className="text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.14]">
              The numbers and the structure disagree, and that is the most useful thing on this
              page.
            </h2>
            <p className="text-muted mt-4 text-[0.95rem] leading-[1.65]">
              A run reports both reads and refuses to reconcile them into a single verdict chip. A
              model that agrees with itself has told you one thing; this has told you two.
            </p>
            <p className="text-muted mt-3 text-[0.95rem] leading-[1.65]">
              The three experiments in the queue are the ones that separate the two readings.
            </p>
          </InkPanel>

          <InkPanel>
            <h3 className="eyebrow font-mono text-accent">Outcome band</h3>
            <p className="mt-2 text-[clamp(2.5rem,6vw,3.5rem)] leading-none font-sans font-bold tracking-tight">
              31%
            </p>
            <p className="text-muted mt-3 text-[0.95rem] leading-[1.6]">
              chance of clearing the threshold <em>you</em> set, by the month you set — measured as
              the share of simulated futures that got there.
            </p>
            <div className="mt-6">
              <OutcomeBand />
            </div>
            <p className="text-muted mt-4 text-[0.8125rem] leading-[1.6]">
              Illustrative shape from a golden test fixture. A live run is always labelled live.
            </p>
          </InkPanel>
        </div>
      </Section>

      {/* Kill-lines */}
      <Section tone="ink">
        <Reveal>
          <Eyebrow>Kill-lines</Eyebrow>
          <Display>An assumption is only useful once it has a number that ends it.</Display>
          <Lede>
            Each of the five to eight load-bearing assumptions gets a threshold, a rung, and the
            probability that the truth sits on the wrong side. That is what makes the list an
            ordered queue rather than a risk register.
          </Lede>
        </Reveal>

        <ScrollableTable label="Kill-line table" className="mt-10">
          <table className="w-full min-w-[46rem] text-left text-[0.95rem]">
            <caption className="sr-only">
              Structure of the kill-line table, with the values left generic
            </caption>
            <thead>
              <tr className="border-line border-b">
                {['Assumption', 'Kill-line', 'Evidence', 'Wrong side'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="eyebrow font-mono text-muted py-3 pr-6 font-bold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {KILL_LINES.map((row) => (
                <tr key={row.assumption} className="border-line border-b">
                  <th scope="row" className="py-4 pr-6 font-normal">
                    {row.assumption}
                  </th>
                  <td className={`py-4 pr-6 ${row.rung ? '' : 'font-mono text-muted text-xs'}`}>
                    {row.line}
                  </td>
                  <td className="py-4 pr-6">
                    {row.rung ? (
                      <span className="flex items-center gap-2">
                        <RungBadge rung={row.rung} />
                        {row.evidence}
                      </span>
                    ) : (
                      <span className="text-muted">{row.evidence}</span>
                    )}
                  </td>
                  <td className="text-muted py-4">{row.side}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>

        <p className="text-muted mt-6 text-sm">
          Structure of the table, with the values left generic. Your run carries real thresholds and
          real sources.
        </p>
      </Section>

      {/* Fit */}
      <Section tone="night">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="border-l-2 border-positive-dark pl-5 sm:pl-6">
            <h2 className="eyebrow font-mono text-positive mb-5">Use it when</h2>
            <ul className="flex flex-col gap-4">
              {USE_WHEN.map((item) => (
                <li key={item} className="leading-[1.65]">
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="border-l-2 border-red-bright pl-5 sm:pl-6">
            <h2 className="eyebrow font-mono text-accent mb-5">Do not use it for</h2>
            <ul className="flex flex-col gap-4">
              {DO_NOT_USE.map((item) => (
                <li key={item} className="leading-[1.65]">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Engine attestation */}
      <Section tone="red">
        <Reveal>
          <Eyebrow>Engine attestation</Eyebrow>
          <Display>
            We publish the smaller number, because the smaller number is the one you can check.
          </Display>
          <Lede className="!text-muted">
            Every registered swarm class in the engine was instantiated and executed against two
            maximally different inputs, twice. The audit&apos;s verdicts, not the marketing counts,
            are what the Startup Idea Validator is allowed to publish.
          </Lede>
        </Reveal>

        <dl className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {ATTESTATION_STATS.map((stat) => (
            <div key={stat.label}>
              <dt className="sr-only">{stat.label}</dt>
              <dd>
                <p className="text-gradient text-[clamp(2.5rem,5.5vw,3.5rem)] leading-none font-sans font-bold tracking-tight">
                  <Counter value={stat.value} prefix={stat.prefix ?? ''} />
                </p>
                <p className="mt-2 font-semibold">{stat.label}</p>
                <p className="mt-2 text-[0.9375rem] leading-[1.6] text-muted">{stat.body}</p>
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-12 grid gap-8 border-t border-line pt-8 lg:grid-cols-2">
          <div>
            <p className="font-mono mb-2 inline-block bg-surface-3 px-2 py-0.5 text-[0.6875rem] font-bold tracking-[0.14em] uppercase">
              ⚠ Material caveat
            </p>
            <h3 className="font-sans text-lg font-bold tracking-normal">
              Real engine, simulated fuel
            </h3>
            <p className="mt-2 text-[0.9375rem] leading-[1.6] text-muted">
              The engine seeds its perception from a hash of the query text rather than a semantic
              embedding. So the Startup Idea Validator does not claim the swarm read your idea. Comprehension is done by
              stage calls under binding output contracts; the engine contributes forward dynamics
              over quantities the Startup Idea Validator derived.
            </p>
          </div>
          <div>
            <p className="font-mono mb-2 inline-block bg-surface-3 px-2 py-0.5 text-[0.6875rem] font-bold tracking-[0.14em] uppercase">
              ⚠ Material caveat
            </p>
            <h3 className="font-sans text-lg font-bold tracking-normal">
              Axiom gate: not deployed
            </h3>
            <p className="mt-2 text-[0.9375rem] leading-[1.6] text-muted">
              The package is complete and its imports resolve, but the daemon is not running in
              production. Attestation reports it as{' '}
              <code className="font-mono text-[0.85em]">not_deployed</code>. It never reports a pass
              for a gate that is not running.
            </p>
          </div>
        </div>

        <p className="mt-8">
          <ArrowLink to="/engine">The full attestation</ArrowLink>
        </p>
      </Section>

      {/* Refusal */}
      <Section tone="ink">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <Eyebrow>Refusal</Eyebrow>
            <Display>Some ideas get a refusal instead of a report.</Display>
            <Lede>
              A refusal is a successful outcome. It arrives in the same furniture as a finished
              report — no error chrome — because dressing it as a failure is how a product learns to
              fabricate.
            </Lede>
            <p className="mt-4 font-semibold">You are not charged for a refused run.</p>
          </Reveal>

          <Reveal delay={80}>
            <Card>
              <h3 className="eyebrow font-mono text-accent mb-4">Refused · what would fix it</h3>
              <dl>
                {[
                  {
                    t: 'Threshold',
                    d: 'One sentence naming the metric, the value, and the month by which it must hold.',
                  },
                  { t: 'Buyer', d: 'Who pays, and out of which budget.' },
                  { t: 'Mechanism', d: 'How the product reaches that buyer the first time.' },
                ].map((row) => (
                  <div
                    key={row.t}
                    className="border-line grid grid-cols-[7rem_1fr] gap-4 border-b py-3 last:border-b-0"
                  >
                    <dt className="font-bold">{row.t}</dt>
                    <dd className="text-muted text-[0.95rem] leading-[1.6]">{row.d}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-muted mt-4 text-[0.9375rem]">
                Two sentences is usually the whole fix. Resubmit and the run proceeds.
              </p>
            </Card>
          </Reveal>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="mist">
        <Eyebrow>Common questions</Eyebrow>
        <Display>Before you submit</Display>
        <div className="mt-8 max-w-4xl">
          <Accordion items={FAQS} idPrefix="home-faq" />
        </div>
        <p className="text-muted mt-8">
          <ArrowLink to="/faq">All sixteen answers</ArrowLink>
        </p>
      </Section>

      {/* CTA */}
      <Section tone="red" className="text-center">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-[clamp(1.875rem,4.6vw,3.25rem)]">
            Bring the idea and the threshold you would walk away from.
          </h2>
          <p className="mx-auto mt-5 max-w-[46ch] text-lg leading-[1.6] text-muted">
            The rest is our job. If your idea is too vague to test, you will hear that in minutes
            and pay nothing.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <ButtonLink to="/contact">Submit an idea</ButtonLink>
            <ButtonLink to="/pricing" variant="secondary">
              See pricing
            </ButtonLink>
          </div>
        </div>
      </Section>
    </>
  );
}

/** Decorative stack of rotated frames from the reference hero. */
function HeroDiagram() {
  return (
    <DrawOnView className="text-muted mx-auto hidden w-full max-w-lg md:block">
      <svg viewBox="0 0 460 440" aria-hidden="true" focusable="false" className="w-full">
        <defs>
          <linearGradient id="frame-a" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1f6feb" stopOpacity="1" />
            <stop offset="100%" stopColor="#12a870" stopOpacity="0.65" />
          </linearGradient>
          <linearGradient id="frame-b" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f5a623" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#1f6feb" stopOpacity="0.55" />
          </linearGradient>
        </defs>

        <g fill="none" strokeWidth="1.5">
          <rect
            data-draw=""
            x="52"
            y="46"
            width="330"
            height="286"
            rx="14"
            stroke="url(#frame-a)"
            transform="rotate(-7 217 189)"
          />
          <rect
            data-draw=""
            x="82"
            y="76"
            width="330"
            height="286"
            rx="14"
            stroke="url(#frame-b)"
            transform="rotate(3 247 219)"
          />
          <rect
            data-draw=""
            x="66"
            y="106"
            width="330"
            height="286"
            rx="14"
            stroke="color-mix(in oklab, var(--muted) 45%, transparent)"
            transform="rotate(-2 231 249)"
          />
        </g>

        <rect x="196" y="176" width="104" height="104" rx="16" fill="#1f6feb" opacity="0.3">
          <animate
            attributeName="opacity"
            values="0.3;0.55;0.3"
            dur="4s"
            repeatCount="indefinite"
          />
        </rect>
        <rect
          x="196"
          y="176"
          width="104"
          height="104"
          rx="16"
          fill="#0f1e33"
          stroke="rgba(255,255,255,0.2)"
        />
        <text
          x="248"
          y="220"
          textAnchor="middle"
          fill="#f2f6fc"
          fontFamily="JetBrains Mono, monospace"
          fontSize="11"
          fontWeight="700"
          letterSpacing="1.6"
        >
          <tspan x="248" dy="0">
            IDEA
          </tspan>
          <tspan x="248" dy="18">
            UNDER
          </tspan>
          <tspan x="248" dy="18">
            TEST
          </tspan>
        </text>

        {[
          { label: 'BUYER', x: 34, y: 132, w: 66 },
          { label: 'EVIDENCE', x: 328, y: 108, w: 88 },
          { label: 'THRESHOLD', x: 20, y: 316, w: 96 },
          { label: 'RISK', x: 350, y: 342, w: 56 },
        ].map((tag) => (
          <g key={tag.label}>
            <rect
              x={tag.x}
              y={tag.y}
              width={tag.w}
              height="24"
              rx="12"
              fill="var(--surface)"
              stroke="var(--line-strong)"
            />
            <text
              x={tag.x + tag.w / 2}
              y={tag.y + 16}
              textAnchor="middle"
              fill="currentColor"
              fontFamily="JetBrains Mono, monospace"
              fontSize="9.5"
              fontWeight="700"
              letterSpacing="1.4"
            >
              {tag.label}
            </text>
          </g>
        ))}

        <circle cx="404" cy="196" r="4" fill="#f5a623">
          <animate attributeName="r" values="4;7;4" dur="2.6s" repeatCount="indefinite" />
        </circle>
      </svg>
    </DrawOnView>
  );
}
